import { useState, type FormEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import type { LoginResponse } from '@bhops/shared';
import { authApi } from '@/api/auth';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/components/Toast';

export function LoginPage() {
  // windows-ux-edit (2026-06, 슬라이드1) — 이메일 기억(localStorage). 비번은 저장 안 함.
  const [email, setEmail] = useState(() => localStorage.getItem('bhops.rememberEmail') ?? '');
  const [rememberEmail, setRememberEmail] = useState(
    () => localStorage.getItem('bhops.rememberEmail') != null,
  );
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // #12 — when /auth/login answers status:'2fa_required' we hold the short-lived
  // challenge token and switch to a TOTP step instead of crashing on the absent
  // user/tokens. The 6-digit code is traded for a real session via /auth/login/2fa.
  const [mfaChallengeToken, setMfaChallengeToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  // docs/plan/google-sso-auth-2026-06 — Google SSO 기본, 비번 폼은 "관리자 로그인"
  // 토글 뒤로(admin break-glass). VITE_GOOGLE_CLIENT_ID 미설정 환경(로컬/CI)에서는
  // Google 버튼이 없으니 곧장 비번 폼을 보여준다.
  const googleEnabled = !!import.meta.env.VITE_GOOGLE_CLIENT_ID;
  const [showAdminLogin, setShowAdminLogin] = useState(!googleEnabled);

  const navigate = useNavigate();
  const location = useLocation();
  const setSession = useAuthStore((s) => s.setSession);
  const toast = useToast();

  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/';

  function completeLogin(session: LoginResponse): void {
    setSession(session);
    toast.success(`${session.user.displayName} 님, 환영합니다.`, '로그인 성공');
    navigate(redirectTo, { replace: true });
  }

  // docs/plan/google-sso-auth-2026-06 — Google ID 토큰을 백엔드로 보내 세션 발급.
  async function handleGoogle(cred: CredentialResponse): Promise<void> {
    if (!cred.credential) {
      setError('Google 로그인에 실패했습니다. 다시 시도해 주세요.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const session = await authApi.loginWithGoogle(cred.credential);
      completeLogin(session);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await authApi.login({ email: email.trim(), password });
      // windows-ux-edit (2026-06, 슬라이드1) — 자격증명 수락 시 이메일 기억/해제.
      if (rememberEmail) localStorage.setItem('bhops.rememberEmail', email.trim());
      else localStorage.removeItem('bhops.rememberEmail');
      if (result.status === '2fa_required') {
        // No session yet — show the TOTP step. password stays in state in case
        // the user backs out via cancelMfa.
        setMfaChallengeToken(result.challengeToken);
        setCode('');
        return;
      }
      completeLogin(result);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function submitMfa(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!mfaChallengeToken) return;
    setError(null);
    setSubmitting(true);
    try {
      const session = await authApi.login2fa({
        challengeToken: mfaChallengeToken,
        code: code.trim(),
      });
      completeLogin(session);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  function cancelMfa(): void {
    setMfaChallengeToken(null);
    setCode('');
    setPassword('');
    setError(null);
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-canvas)',
        padding: 24,
      }}
    >
      <div
        style={{
          width: 400,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--r-card)',
          padding: 32,
          boxShadow: 'var(--shadow-md)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            marginBottom: 28,
          }}
        >
          <img src="/logo-monogram.svg" width={40} height={40} alt="Beyond Honeycomb" />
          <div>
            <div className="kuun-h3" style={{ margin: 0 }}>
              Beyond Honeycomb Console
            </div>
            <div className="kuun-caption" style={{ marginTop: 2 }}>
              사업, 제조, 필드를 한곳에서 관리하는 운영 도구
            </div>
          </div>
        </div>

        {!mfaChallengeToken && googleEnabled && !showAdminLogin && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
              <GoogleLogin
                onSuccess={handleGoogle}
                onError={() => setError('Google 로그인에 실패했습니다.')}
                text="signin_with"
                shape="rectangular"
                width="336"
              />
            </div>
            <div
              className="kuun-caption"
              style={{ textAlign: 'center', color: 'var(--fg-subtle)' }}
            >
              회사 Google 계정으로 로그인해 주세요.
            </div>
            {error && (
              <div
                role="alert"
                style={{
                  marginTop: 16,
                  padding: '8px 10px',
                  borderRadius: 'var(--r-btn)',
                  fontSize: 13,
                  background: 'var(--bg-danger-subtle)',
                  border: '1px solid var(--fg-danger)',
                  color: 'var(--fg-danger)',
                }}
              >
                {error}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                setShowAdminLogin(true);
                setError(null);
              }}
              style={{
                marginTop: 16,
                width: '100%',
                height: 36,
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--r-btn)',
                background: 'var(--bg-surface)',
                color: 'var(--fg-muted)',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              관리자 비밀번호로 로그인
            </button>
          </div>
        )}

        {!mfaChallengeToken && (!googleEnabled || showAdminLogin) && (
          <form onSubmit={submit}>
            <label
              className="kuun-label"
              style={{ display: 'block', marginBottom: 6 }}
              htmlFor="email"
            >
              이메일
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
              placeholder="name@company.com"
              style={inputStyle}
            />

            <label
              className="kuun-label"
              style={{ display: 'block', margin: '16px 0 6px' }}
              htmlFor="password"
            >
              비밀번호
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              style={inputStyle}
            />

            {/* windows-ux-edit (2026-06, 슬라이드1) — 이메일 기억 체크박스 */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                margin: '14px 0 0',
                fontSize: 13,
                color: 'var(--fg-muted)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={rememberEmail}
                onChange={(e) => setRememberEmail(e.target.checked)}
              />
              이메일 기억
            </label>

            {error && (
              <div
                role="alert"
                style={{
                  marginTop: 16,
                  padding: '8px 10px',
                  borderRadius: 'var(--r-btn)',
                  fontSize: 13,
                  background: 'var(--bg-danger-subtle)',
                  border: '1px solid var(--fg-danger)',
                  color: 'var(--fg-danger)',
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                marginTop: 20,
                width: '100%',
                height: 40,
                border: 'none',
                borderRadius: 'var(--r-btn)',
                background: submitting ? 'var(--brand-active)' : 'var(--brand)',
                boxShadow: submitting ? 'none' : 'var(--shadow-brand)',
                color: 'var(--brand-on)',
                fontWeight: 600,
                fontSize: 14,
                cursor: submitting ? 'wait' : 'pointer',
                transition: 'background var(--dur-fast) var(--ease-standard)',
              }}
            >
              {submitting ? '로그인 중…' : '로그인'}
            </button>

            {googleEnabled && (
              <button
                type="button"
                onClick={() => {
                  setShowAdminLogin(false);
                  setError(null);
                }}
                style={{
                  marginTop: 12,
                  width: '100%',
                  height: 36,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--fg-muted)',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                ← Google 로그인으로
              </button>
            )}
          </form>
        )}

        {mfaChallengeToken && (
          <form onSubmit={submitMfa}>
            <div className="kuun-h3" style={{ margin: '0 0 6px' }}>
              2단계 인증
            </div>
            <div className="kuun-caption" style={{ marginBottom: 16 }}>
              인증 앱에 표시된 6자리 코드를 입력해 주세요.
            </div>

            <label
              className="kuun-label"
              style={{ display: 'block', marginBottom: 6 }}
              htmlFor="mfa-code"
            >
              인증 코드
            </label>
            <input
              id="mfa-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              style={{
                ...inputStyle,
                letterSpacing: '0.4em',
                textAlign: 'center',
                fontSize: 18,
              }}
            />

            {error && (
              <div
                role="alert"
                style={{
                  marginTop: 16,
                  padding: '8px 10px',
                  borderRadius: 'var(--r-btn)',
                  fontSize: 13,
                  background: 'var(--bg-danger-subtle)',
                  border: '1px solid var(--fg-danger)',
                  color: 'var(--fg-danger)',
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting || code.length !== 6}
              style={{
                marginTop: 20,
                width: '100%',
                height: 40,
                border: 'none',
                borderRadius: 'var(--r-btn)',
                background:
                  submitting || code.length !== 6 ? 'var(--brand-active)' : 'var(--brand)',
                boxShadow: submitting || code.length !== 6 ? 'none' : 'var(--shadow-brand)',
                color: 'var(--brand-on)',
                fontWeight: 600,
                fontSize: 14,
                cursor: submitting ? 'wait' : 'pointer',
                transition: 'background var(--dur-fast) var(--ease-standard)',
              }}
            >
              {submitting ? '인증 중…' : '인증'}
            </button>

            <button
              type="button"
              onClick={cancelMfa}
              disabled={submitting}
              style={{
                marginTop: 10,
                width: '100%',
                height: 36,
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--r-btn)',
                background: 'var(--bg-surface)',
                color: 'var(--fg-muted)',
                fontSize: 13,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              ← 다른 계정으로 로그인
            </button>
          </form>
        )}

        <div
          className="kuun-caption"
          style={{
            marginTop: 20,
            textAlign: 'center',
            color: 'var(--fg-subtle)',
          }}
        >
          Beyond Honeycomb Console
        </div>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 40,
  padding: '0 12px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  fontSize: 14,
  background: 'var(--bg-sunken)',
  color: 'var(--fg-default)',
};
